import cx from 'classnames';
import Image from 'next/image';
import { useCallback } from 'react';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../design-system/components/form/button/Button';
import { ImageCopyright } from '../../../design-system/components/image/copyright/ImageCopyright';
import { RATIO, Ratio } from '../../../design-system/components/ratio/Ratio';
import { DoublePictureWithDescriptionSlice } from '../../../domains/Prismic/DestinationDetailDocument';
import { EvaneosPrismicRichText } from '../../../domains/Prismic/EvaneosPrismicRichText';
import { NullableSlice } from '../../../domains/Prismic/Utilities';

import style from './DoublePictureWithDescription.module.css';

export type DoublePictureWithDescriptionProps = {
    doublePictureWithDescription: NullableSlice<DoublePictureWithDescriptionSlice>;
    row: number;
};

export function DoublePictureWithDescription({
    doublePictureWithDescription,
    row,
}: DoublePictureWithDescriptionProps) {
    const {
        firstPicture,
        firstPictureCopyright,
        secondPicture,
        secondPictureCopyright,
        title,
        text,
        link,
    } = doublePictureWithDescription;

    const { trackEvent } = useTrackEventAmplitude();
    const handleClickButton = useCallback(() => {
        trackEvent((ampli, defaultProperties) => {
            ampli.marketingDestinationDetailsCtaClicked({
                ...defaultProperties,
                block: 'double_picture_with_description',
                url_redirection: link?.url ?? '',
                row_number: row,
            });
        });
    }, [link?.url, row, trackEvent]);

    return (
        <div className={style.wrapper}>
            <div className={style.imagesContainer}>
                {firstPicture?.url ? (
                    <Ratio ratio={RATIO['16/10']}>
                        <Image
                            className={style.image}
                            alt={firstPicture.alt}
                            src={firstPicture.url}
                            unoptimized
                            fill
                        />

                        {firstPictureCopyright ? (
                            <ImageCopyright copyright={firstPictureCopyright} />
                        ) : null}
                    </Ratio>
                ) : null}
                {secondPicture?.url ? (
                    <Ratio ratio={RATIO['1/1']}>
                        <Image
                            className={style.image}
                            alt={secondPicture.alt}
                            src={secondPicture.url}
                            unoptimized
                            fill
                        />

                        {secondPictureCopyright ? (
                            <ImageCopyright copyright={secondPictureCopyright} />
                        ) : null}
                    </Ratio>
                ) : null}
            </div>
            <div className={style.contentContainer}>
                {title ? (
                    <h2 className={cx('font-secondary-s-regular', style.title)}>{title}</h2>
                ) : null}
                {text && text.length > 0 ? <EvaneosPrismicRichText field={text} /> : null}
                {link?.url && link?.label ? (
                    <Button href={link.url} onClick={handleClickButton}>
                        {link.label}
                    </Button>
                ) : null}
            </div>
        </div>
    );
}
